import { Box, Chip, Paper, Typography } from '@mui/material'
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium'
import DataGridToolbar from 'src/v3/components/data-grid-toolbar'
import dayjs from 'dayjs'
import { useCallback } from 'react'

import { useAppSelector } from 'src/store/hooks'
import { DownloadIcon } from 'src/v3/core/icons'
import { selectCeActivities } from '../../index'

export default function ExportView() {
  let ExportViewToolbar = useCallback(
    () => <DataGridToolbar fileName="ce-activities-export" />,
    [],
  )

  // Redux selectors
  let ceActivities = useAppSelector(selectCeActivities)

  // Grid columns for export view - from original component
  let gridColumns: GridColDef[] = [
    { field: 'userName', headerName: 'User Name', width: 200 },
    { field: 'userEmail', headerName: 'Email', width: 200 },
    { field: 'courseTitle', headerName: 'Course/Activity', width: 300 },
    {
      field: 'completionDate',
      headerName: 'Completion Date',
      width: 150,
      renderCell: (params) => (
        <span>{dayjs(params.value).format('MMM DD, YYYY')}</span>
      ),
    },
    { field: 'ceHours', headerName: 'CE Hours', width: 100, type: 'number' },
    { field: 'category', headerName: 'Category', width: 150 },
    {
      field: 'licenseTypes',
      headerName: 'License Types',
      width: 150,
      renderCell: (params) => (
        <span>{params.value?.join(', ') || 'Unknown'}</span>
      ),
    },
    {
      field: 'activityType',
      headerName: 'Type',
      width: 120,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value}
          color={
            params.value === 'recorded'
              ? 'primary'
              : params.value === 'live'
                ? 'secondary'
                : 'default'
          }
          variant="outlined"
        />
      ),
    },
    { field: 'courseCode', headerName: 'Course Code', width: 120 },
    { field: 'certificateId', headerName: 'Certificate ID', width: 150 },
    {
      field: 'wasAssigned',
      headerName: 'Was Assigned',
      width: 120,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value ? 'Yes' : 'No'}
          color={params.value ? 'secondary' : 'default'}
          variant="outlined"
        />
      ),
    },
    { field: 'assignmentName', headerName: 'Assignment Name', width: 200 },
    {
      field: 'assignmentDueDate',
      headerName: 'Assignment Due Date',
      width: 150,
      renderCell: (params) => (
        <span>
          {params.value ? dayjs(params.value).format('MMM DD, YYYY') : '-'}
        </span>
      ),
    },
  ]

  return (
    <Paper sx={{ p: 3, maxHeight: 600, overflow: 'auto' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography
          variant="h6"
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <DownloadIcon />
          Export Data
        </Typography>
        {ceActivities.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            {ceActivities.length} activities •{' '}
            {new Set(ceActivities.map((a) => a.userId)).size} users
          </Typography>
        )}
      </Box>
      {ceActivities.length === 0 ? (
        <Box
          sx={{
            height: 400,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            color: 'text.secondary',
          }}
        >
          <DownloadIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            No Data to Export
          </Typography>
          <Typography variant="body2">
            No CE activities found for the selected criteria. Try adjusting your
            filters or date range.
          </Typography>
        </Box>
      ) : (
        <DataGridPremium
          rows={ceActivities}
          columns={gridColumns}
          slots={{ toolbar: ExportViewToolbar }}
          showToolbar
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          pageSizeOptions={[25, 50, 100]}
          checkboxSelection
          disableRowSelectionOnClick
        />
      )}
    </Paper>
  )
}
